import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, Paper, Stack } from '@mui/material';
import Typography from '../i18n/LocalizedTypography';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { useTranslation } from 'react-i18next';
import { sadeSatiPhaseText, sadeSatiSectionTitle, sadeSatiSignText, sadeSatiText } from '../../i18n/sadeSati';

type Props = { apiBaseUrl: string; chartId?: string; ayanamshaMode: string; token?: string | null };

const PHASE_COLORS: Record<string, string> = {
  rising: '#D97706', peak: '#DC2626', setting: '#7C3AED', fourth: '#0284C7', eighth: '#475569',
};

export default function SadeSatiPanel({ apiBaseUrl, chartId, ayanamshaMode, token }: Props) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const text = useCallback((key: Parameters<typeof sadeSatiText>[1], params?: Record<string, string | number | null | undefined>) => sadeSatiText(language, key, params), [language]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!chartId) return;
    let cancelled = false;
    setLoading(true); setError('');
    axios.get(`${apiBaseUrl}/charts/${chartId}/sade-sati`, {
      params: { ayanamshaMode, calculationModule: 'vedic' },
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }).then((response) => {
      if (!cancelled) setData(response.data?.data?.sadeSati || response.data?.data);
    }).catch(() => {
      if (!cancelled) setError(text('failed'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [apiBaseUrl, ayanamshaMode, chartId, token, text]);

  const cycles = useMemo(() => (data?.sadeSatiPeriods || []).reduce((groups: Record<string, any[]>, period: any) => {
    const key = String(period.cycle || 1);
    (groups[key] ||= []).push(period);
    return groups;
  }, {}), [data]);

  if (!chartId) return <Alert severity="info">{text('requiresSaved')}</Alert>;
  if (loading) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /><Typography color="text.secondary" sx={{ mt: 2 }}>{text('calculating')}</Typography></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const renderPeriod = (period: any, key: string) => {
    const accent = PHASE_COLORS[period.phase] || '#2E7D32';
    return <Card key={key} variant="outlined" sx={{ borderLeft: `5px solid ${accent}`, height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Typography fontWeight={800}>{sadeSatiPhaseText(language, period.phase, 'label')}</Typography>
          {period.isCurrent ? <Chip label={text('current')} color="success" size="small" /> : null}
        </Stack>
        <Typography color="var(--app-ink-primary)" fontWeight={700} sx={{ mt: 0.5 }}>{text('saturnPosition', { sign: sadeSatiSignText(language, period.saturnSignName), house: period.houseFromMoon })}</Typography>
        <Typography sx={{ mt: 0.75 }}>{formatDisplayDate(period.startDate)} – {formatDisplayDate(period.endDate)}</Typography>
        <Typography variant="caption" color="text.secondary">{text('age', { start: period.startAge, end: period.endAge })}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{sadeSatiPhaseText(language, period.phase, 'focus')}</Typography>
      </CardContent>
    </Card>;
  };

  return <Box>
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: 'primary.light' }}>
      <Typography variant="h5" color="var(--app-ink-primary)" fontWeight={900}>{text('title')}</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5 }}>{text('description')}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
        <Chip label={`${text('natalMoon')}: ${sadeSatiSignText(language, data.natalMoon?.signName)}`} color="primary" variant="outlined" />
        <Chip label={`${data.ayanamshaMode} Ayanamsha`} variant="outlined" />
        <Chip label={`${formatDisplayDate(data.analysisRange?.startDate)} – ${formatDisplayDate(data.analysisRange?.endDate)}`} variant="outlined" />
      </Stack>
    </Paper>

    <Typography variant="h6" color="var(--app-ink-primary)" fontWeight={900} sx={{ mt: 3 }}>{sadeSatiSectionTitle(language, 'sadeSati')}</Typography>
    <Typography color="text.secondary">{text('sadeSummary', { count: data.summary?.sadeSatiCycles || 0 })}</Typography>
    {Object.entries(cycles).map(([cycle, periods]) => <Box key={cycle} sx={{ mt: 2 }}>
      <Typography fontWeight={800} sx={{ mb: 1 }}>{text('cycle', { cycle })}</Typography>
      <Grid container spacing={1.5}>{(periods as any[]).map((period, index) => <Grid key={`${cycle}-${index}`} size={{ xs: 12, md: 6, lg: 4 }}>{renderPeriod(period, `sade-${cycle}-${index}`)}</Grid>)}</Grid>
    </Box>)}

    <Typography variant="h6" color="var(--app-ink-primary)" fontWeight={900} sx={{ mt: 4 }}>{sadeSatiSectionTitle(language, 'dhaiya')}</Typography>
    <Typography color="text.secondary" sx={{ mb: 1.5 }}>{text('dhaiyaDescription')}</Typography>
    <Grid container spacing={1.5}>{(data.dhaiyaPeriods || []).map((period: any, index: number) => <Grid key={index} size={{ xs: 12, md: 6, lg: 4 }}>{renderPeriod(period, `dhaiya-${index}`)}</Grid>)}</Grid>

  </Box>;
}
